import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Calendar, Hammer, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Process = () => {
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useTranslation();
  
  const processSteps = t('process.steps', { returnObjects: true }) as any;
  const stepsArray = Array.isArray(processSteps) ? processSteps : [];
  
  const steps = stepsArray.map((step, index) => {
    const icons = [MessageCircle, Calendar, Hammer, FileText];
    const durations = [
      t('process.durations.step1'),
      t('process.durations.step2'),
      t('process.durations.step3'),
      t('process.durations.step4')
    ];
    return { ...step, icon: icons[index], duration: durations[index] };
  });

  return (
    <section id="process" className="py-24 bg-white">
        <div className="container mx-auto px-4">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true, amount: 0.5 }}
                className="text-center md:text-left max-w-3xl mb-14"
            >
                <h2 className="text-3xl md:text-4xl font-bold">{t('process.title')}</h2>
                <p className="text-lg text-gray-600 mt-4">
                    {t('process.subtitle')}
                </p>
            </motion.div>
      
            {/* Desktop: Horizontal Timeline */}
            <div className="hidden md:block">
                {/* Steps Icons */}
                <div className="grid grid-cols-4 gap-8 mb-8 relative">
                    {/* Progress Bar */}
                    <div className="absolute top-1/2 -translate-y-1/2 left-0 rtl:left-auto rtl:right-0 w-full h-1 bg-gray-200">
                        <motion.div
                            className="h-full bg-primary-500"
                            animate={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                        />
                    </div>
                    {steps.map((step, index) => (
                        <motion.div
                        key={index}
                        whileHover={{ scale: 1.1 }}
                        onClick={() => setActiveStep(index)}
                        className="cursor-pointer text-center relative z-10"
                        >
                        <motion.div
                            animate={{
                            backgroundColor: activeStep >= index ? "#3B82F6" : "#F3F4F6",
                            color: activeStep >= index ? "#FFFFFF" : "#6B7280"
                            }}
                            transition={{ duration: 0.3 }}
                            className="w-20 h-20 border-4 border-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg"
                        >
                            <step.icon className="w-8 h-8" />
                        </motion.div>
                        </motion.div>
                    ))}
                </div>

                {/* Content Display */}
                <div className="relative h-48">
                {steps.map((step, index) => (
                     <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: activeStep === index ? 1 : 0, y: activeStep === index ? 0 : 20 }}
                        transition={{ duration: 0.5 }}
                        className="absolute w-full text-center"
                    >
                        <h4 className="font-bold text-xl mb-2">{step.title}</h4>
                        <p className="text-gray-600 max-w-md mx-auto">{step.description}</p>
                        <span className="text-sm text-primary-500 font-medium mt-2 inline-block">
                           {t('process.duration_prefix')}: {step.duration}
                        </span>
                    </motion.div>
                ))}
                </div>
            </div>

             {/* Mobile: Vertical Timeline */}
            <div className="md:hidden">
                {steps.map((step, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.2, duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                    className="flex gap-6 mb-8 last:mb-0"
                >
                    {/* Timeline Line */}
                    <div className="flex flex-col items-center">
                    <div
                        className="w-16 h-16 rounded-full flex items-center justify-center bg-primary-500"
                    >
                        <step.icon className="w-8 h-8 text-white" />
                    </div>
                    {index < steps.length - 1 && (
                        <div className="w-0.5 flex-grow bg-gray-300 my-2" />
                    )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pt-3">
                        <h4 className="font-semibold text-xl mb-1">{step.title}</h4>
                        <p className="text-gray-600 mb-2">{step.description}</p>
                        <span className="text-xs text-primary-600 font-medium bg-primary-50 px-2 py-1 rounded">
                            {step.duration}
                        </span>
                    </div>
                </motion.div>
                ))}
            </div>
        </div>
    </section>
  );
};
